// src/components/PatientStatusDashboard.js
// Author: Sunil Jain
// FINAL: This version contains the upgraded ComplianceWidget with a 7/30 day toggle.

import React, { useState } from 'react';
import { Grid, Paper, Typography, Box, useTheme, alpha, Stack, Tooltip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import ComplianceProgressCircle from './ComplianceProgressCircle';

// --- Helper Functions & Style Definitions ---
const getPriorityStyle = (theme, score) => {
    if (score >= 30) return { color: theme.palette.error.main, status: 'Urgent' };
    if (score >= 21) return { color: theme.palette.warning.dark, status: 'Elevated' };
    if (score >= 13) return { color: theme.palette.primary.main, status: 'Guarded' };
    if (score >= 5) return { color: theme.palette.success.main, status: 'Low' };
    return { color: theme.palette.text.secondary, status: 'Normal' };
};

const getPriorityReason = (score) => {
    if (score >= 21) return "Due to abnormal vitals & symptom reports.";
    if (score >= 13) return "Based on recent symptom reports.";
    if (score >= 5) return "Monitoring recent check-in data.";
    return "All metrics are within normal limits.";
};

// --- Child Components (Widgets) ---
const WidgetCard = ({ children, sx = {}, onClick }) => (
    <Grid item xs={12} sm={6} md={4} sx={{ minHeight: '160px' }}>
        <Paper
            elevation={0}
            variant="outlined"
            onClick={onClick}
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                borderRadius: 2.5,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                textAlign: 'center',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                    transform: onClick ? 'translateY(-2px)' : 'none',
                    boxShadow: onClick ? (theme) => `0 4px 12px ${alpha(theme.palette.grey[500], 0.15)}` : 'none',
                },
                ...sx
            }}
        >
            {children}
        </Paper>
    </Grid>
);

const ReviewPriorityWidget = ({ score = 0 }) => {
    const theme = useTheme();
    const { color, status } = getPriorityStyle(theme, score);
    const reason = getPriorityReason(score);

    return (
        <WidgetCard sx={{ borderColor: color, borderWidth: 2 }}>
             <Typography variant="subtitle2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                REVIEW PRIORITY
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color, textTransform: 'uppercase', my: 1 }}>
                {status}
            </Typography>
            <Tooltip title="This is a calculated suggestion based on recent data.">
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {reason}
                </Typography>
            </Tooltip>
        </WidgetCard>
    );
};

const HealthPulseWidget = ({ data = [], onClick }) => {
    const theme = useTheme();
    const trend = data.length > 1 ? data[data.length - 1].score - data[0].score : 0;
    const TrendIcon = trend > 0 ? ArrowUpwardIcon : ArrowDownwardIcon;
    const trendColor = trend > 0 ? theme.palette.success.dark : trend < 0 ? theme.palette.error.dark : 'text.secondary';

    return (
        <WidgetCard sx={{ textAlign: 'left' }} onClick={onClick}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                HEALTH PULSE
            </Typography>
            <Box sx={{ width: '100%', height: 60, my: 1, ml: '-16px' }}>
                <ResponsiveContainer>
                    <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                         <defs>
                            <linearGradient id="pulseGradientLight" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.4}/>
                                <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <Area type="monotone" dataKey="score" stroke={theme.palette.primary.main} strokeWidth={2} fill="url(#pulseGradientLight)" />
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <TrendIcon sx={{ color: trendColor, fontSize: '1rem', mr: 0.5 }} />
                <Typography variant="body2" sx={{ color: trendColor, fontWeight: 'medium' }}>
                    {trend > 0 ? 'Improving' : trend < 0 ? 'Declining' : 'Stable'}
                </Typography>
            </Box>
        </WidgetCard>
    );
};

const ComplianceWidget = ({ data, onMedsClick, onCheckinsClick }) => {
    // Directly use the scores from the simple API response.
    const medsScore = data?.meds ?? 0;
    const checkinsScore = data?.checkins ?? 0;
    
    return (
    <WidgetCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'medium', color: 'text.secondary', textAlign: 'left' }}>
                COMPLIANCE (7-Day)
            </Typography>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
            <Tooltip title="View Medication Adherence Details">
                <Box onClick={onMedsClick} sx={{ textAlign: 'center', cursor: 'pointer', p: 1, borderRadius: 2, '&:hover': { bgcolor: alpha("#000", 0.04) }}}>
                    <ComplianceProgressCircle value={medsScore} />
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Meds</Typography>
                </Box>
            </Tooltip>
            <Tooltip title="View Daily Check-in Details">
                 <Box onClick={onCheckinsClick} sx={{ textAlign: 'center', cursor: 'pointer', p: 1, borderRadius: 2, '&:hover': { bgcolor: alpha("#000", 0.04) }}}>
                    <ComplianceProgressCircle value={checkinsScore} />
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Check-ins</Typography>
                </Box>
            </Tooltip>
        </Stack>
    </WidgetCard>
    );
};


const PatientStatusDashboard = ({ reviewPriorityScore, healthPulseData, complianceData, onMedsClick, onCheckinsClick, onHealthPulseClick }) => {
    return (
        <Box sx={{ width: '100%', mb: 2 }}>
            <Grid container spacing={{ xs: 2, md: 2 }}>
                <ReviewPriorityWidget score={reviewPriorityScore} />
                <HealthPulseWidget data={healthPulseData} onClick={onHealthPulseClick} />
                <ComplianceWidget data={complianceData} onMedsClick={onMedsClick} onCheckinsClick={onCheckinsClick} />
            </Grid>
        </Box>
    );
};

export default PatientStatusDashboard;